import { debounce } from 'throttle-debounce';
import Tooltips from '../../common/tooltips';
import ComparisionTableSlider from './ComparisionTableSlider';

export default class ComparisionTable {
    constructor() {
        this.$container = document.querySelector('.comparison-table');
        this.$list = document.querySelector('.comparison-table__list');
        this.$headerFixed = document.querySelector('.comparison-table__products--fixed');
        this.$legend = document.querySelector('.comparison-table__legend');
        this.$inner = document.querySelector('.comparison-table__inner');

        this.maxHeightOfCells = [];

        this.slider = null;

        if (!this.$container) {
            return;
        }

        this.init();
    }

    init() {
        this.initializeTooltips();
        this.deepEqualizer();
        this.generateLegends();

        this.eventExpandItems();
        this.eventWindowResize();
        this.eventScroll();
        this.setFixedHeaderWidth();

        this.slider = new ComparisionTableSlider(); // init slider

        this.$container.style.opacity = 1;

        this.$container.classList.toggle('comparison-table--is-mobile', window.innerWidth <= 500);
    }

    initializeTooltips() {
        this.$list.querySelectorAll('.tt-trigger').forEach(($trigger) => new Tooltips($trigger));
    }

    /**
     * Helper for iterating through every cell in all columns
     */
    iterateThroughCells(callback = () => { }) {
        const $cols = this.$container.querySelectorAll('.comparison-table__col');

        $cols.forEach(($col, colIndex) => {
            const $cells = $col.querySelectorAll('.comparison-table__cell-header, .comparison-table__cell');

            $cells.forEach(($cell, cellIndex) => callback($cell, cellIndex, colIndex));
        });
    }

    /**
     * Equal height of columns in desktop view
     */
    deepEqualizer(includeLegend = false) {
        this.maxHeightOfCells = [];

        // get the highest height of every cell in column
        this.iterateThroughCells(($cell, index) => {
            $cell.style.height = null;

            const cellHeight = $cell.offsetHeight;

            if (!this.maxHeightOfCells[index]) { // first iteration
                this.maxHeightOfCells[index] = cellHeight;
            } else {
                this.maxHeightOfCells[index] = Math.max(this.maxHeightOfCells[index], cellHeight);
            }
        })

        // set the highest height to cells
        this.iterateThroughCells(($cell, index) => $cell.style.height = this.maxHeightOfCells[index] + 'px');

        if (includeLegend) {
            this.$legend.querySelectorAll('.comparison-table__cell').forEach(($cell, index) => $cell.style.height = this.maxHeightOfCells[index + 1] + 'px')
        }
    }

    /**
     * Generate legends for table (desktop view)
     */
    generateLegends() {
        let output = '';

        if (this.maxHeightOfCells.length === 0) { // before render legend equalize cells
            return;
        }

        const $cells = this.$container.querySelectorAll('.comparison-table__col:first-child .comparison-table__cell');

        $cells.forEach(($cell, index) => {
            const $label = $cell.querySelector('.comparison-table__cell-label');
            const isVisible = $cell.getAttribute('data-is-visible') === 'true';
            const itemHeight = this.maxHeightOfCells[index + 1]; // In array on zero index is height of header. Add +1 to ommit.

            output += `
                <div class="comparison-table__cell" data-is-visible="${String(isVisible)}" style="height: ${itemHeight}px; ${!isVisible ? 'display: none' : ''}">
                    ${$label ? `<span class="comparison-table__cell-label">${$label.innerText}</span>` : ''}
                </div>
            `;
        });

        this.$legend.innerHTML = output;
    }

    /**
     * Expand or collapse cells
     */
    toggleVisibilityCells($itemsToExpand, visibility) {
        if (!$itemsToExpand || visibility === undefined) {
            return;
        }

        [...$itemsToExpand, this.$legend].forEach(($col) => {
            $col.querySelectorAll(`[data-is-visible="false"]`).forEach(($cell) => {
                $cell.style.display = visibility ? 'flex' : 'none';
            });
        });

        this.deepEqualizer(true);
    }

    eventExpandItems() {
        const $btns = this.$container.querySelectorAll('.comparison-table__btn-expand');

        $btns.forEach(($btn) => {
            $btn.addEventListener('click', (e) => {
                const $btn = e.currentTarget;
                let $expandItems = null;

                $btn.classList.toggle('comparison-table__btn-expand--expanded');
                this.$container.classList.toggle('comparison-table--expanded');

                $btn.textContent = $btn.classList.contains('comparison-table__btn-expand--expanded') ? $btn.getAttribute('data-less') : $btn.getAttribute('data-more');

                if ($btn.classList.contains('js-expand--item')) {
                    $expandItems = [$btn.closest('.comparison-table__col')];
                } else if ($btn.classList.contains('js-expand--all')) {
                    $expandItems = this.$list.querySelectorAll('.comparison-table__col');
                }

                this.toggleVisibilityCells($expandItems, $btn.classList.contains('comparison-table__btn-expand--expanded'));
            });
        });
    }

    /**
     * Clear expanded cols and btn below col to default state
     */
    clearExpandedCols() {
        this.toggleVisibilityCells(this.$list.querySelectorAll('.comparison-table__col'), false);

        this.$container.querySelectorAll('.comparison-table__btn-expand').forEach(($btn) => {
            $btn.innerText = $btn.getAttribute('data-more');
            $btn.classList.remove('comparison-table__btn-expand--expanded');
        });

        this.$container.classList.remove('comparison-table--expanded');
    }

    eventWindowResize() {
        window.addEventListener('resize', debounce(200, () => {
            const wWidth = window.innerWidth;

            if (wWidth <= 500 && !this.$container.classList.contains('comparison-table--is-mobile')) {
                this.$container.classList.add('comparison-table--is-mobile');

                this.clearExpandedCols();

                this.$list.style.transform = null;
            } else if (wWidth > 500 && this.$container.classList.contains('comparison-table--is-mobile')) {
                this.$container.classList.remove('comparison-table--is-mobile');

                this.clearExpandedCols();
            }

            if (wWidth > 500) {
                this.deepEqualizer(true);
            }

            this.setFixedHeaderWidth();

            this.slider.toggleNavigation();
        }));
    }

    setFixedHeaderWidth() {
        const tableWidth = this.$inner.getBoundingClientRect().width;
        const headerStyle = getComputedStyle(this.$headerFixed);

        this.$headerFixed.style.width = (tableWidth - parseInt(headerStyle.marginLeft)) + 'px';
    }

    eventScroll() {
        window.addEventListener('scroll', () => {
            let listPosition = this.$list.getBoundingClientRect().top;

            if (document.body.clientWidth >= 1024) {
                if (this.$container.classList.contains('comparison-table--expanded')) {
                    if (listPosition <= 0) {
                        this.$headerFixed.classList.remove('hidden');
                        this.$list.classList.add('no-shadow');
                    } else {
                        this.$headerFixed.classList.add('hidden')
                        this.$list.classList.remove('no-shadow')
                    }
                } else {
                    this.$headerFixed.classList.add('hidden')
                    this.$list.classList.remove('no-shadow')
                }
            } else {
                this.$headerFixed.classList.add('hidden')
                this.$list.classList.remove('no-shadow')
            }
        });
    }
}


// WEBPACK FOOTER //
// ./front/treasurybond/js/components/comparision-table/ComparisionTable.js